import { Prices, DateTime, Uuid, CorporateTravel } from "../../common";
import { AlgomerchTag } from "../../flights/shopping";
import { AirportMap, AirlineMap } from "../../trips-module/itinerary";

export interface TripSummary {
  domestic?: string;
  tripId: string;
  outgoingSlice: TripSlice;
  returningSlice?: TripSlice;
  context: TripSummaryContext;
  tripFares: TripFare[];
}

export interface TripSummaryContext {
  airports: AirportMap;
  airlines: AirlineMap;
}

export interface TripSlice {
  id: string;
  outgoing: boolean;
  departureTime: DateTime;
  originCode: string;
  originName: string;
  arrivalTime: DateTime;
  destinationCode: string;
  destinationName: string;
  stops: number;
  totalDurationMinutes?: number;
  segmentDetails: TripSegment[];
  zonedDepartureTime?: string;
}

export interface SegmentAirline {
  code: string;
  name: string;
}

export interface TripSegment {
  airlineCode: string;
  airlineName: string;
  cabinClassName?: string;
  flightNumber: string;
  marketingAirline: SegmentAirline;
  operatingAirline: SegmentAirline;
  departureTime: DateTime;
  originName: string;
  originCode: string;
  arrivalTime: DateTime;
  destinationName: string;
  destinationCode: string;
  plusDays?: number;
  stops: number;
  stopoverDurationMinutes?: number;
  zonedDepartureTime?: string;
  zonedArrivalTime?: string;
  isSelfTransferLayover?: boolean;
}

export interface FareScore {
  comfort: number;
  value: number;
  isBest: boolean;
  isCheapest: boolean;
  isBestQuality: boolean;
  isFastest: boolean;
}

export interface FareShelfInfo {
  outgoing: FareShelf;
  returning?: FareShelf;
}

export interface FareShelf {
  rating: number;
  brandName: string;
  shortBrandName: string;
  opaqueMapperStep?: string;
}

export interface TripFare {
  amount: Prices;
  fareId: Uuid;
  fareScore: FareScore;
  fareShelf?: FareShelfInfo;
  isMultiTicket: boolean;
}

export interface IFlightGridFareInfo {
  fareId: string;
  tripId?: string;
  fareName: string;
  price: string;
  rawPrice: number;
  prices?: Prices;
  tags: AlgomerchTag[];
  additionalFares?: number;
  corporateTravel?: CorporateTravel;
}

export interface IFlightGridFares {
  basic: IFlightGridFareInfo | null;
  standard: IFlightGridFareInfo | null;
  enhanced: IFlightGridFareInfo | null;
  premium: IFlightGridFareInfo | null;
  luxury: IFlightGridFareInfo | null;
}

export function getFlightGridFares(
  tripFares: TripFare[],
  outgoingRating?: number,
  isReturn?: boolean,
  rewardsKey?: string
) {
  // TODO create currency mapping function
  const fares: IFlightGridFares = {
    basic: null,
    standard: null,
    premium: null,
    enhanced: null,
    luxury: null,
  };
  const filteredFares =
    outgoingRating != undefined
      ? tripFares.filter((f) => f.fareShelf?.outgoing.rating === outgoingRating)
      : tripFares;
  filteredFares.forEach((fare) => {
    const fareShelf = isReturn
      ? fare.fareShelf?.returning
      : fare.fareShelf?.outgoing;
    const tags = getTags(fare.fareScore);
    const reward = fare.amount.rewards[rewardsKey ?? ""];

    const gridFare = {
      fareId: fare.fareId,
      fareName: fareShelf?.shortBrandName ?? "",
      price: `${fare.amount.fiat.currencySymbol}${Math.round(
        fare.amount.fiat.value
      )}`,
      rawPrice: fare.amount.fiat.value,
      tags,
    };

    if (reward) {
      // TODO: possibly move getRewardText and other Prices related helpers to Redmond
      const isFiatCurrency = reward.currency === "$";
      const formattedValue = isFiatCurrency
        ? reward.value.toFixed(2)
        : Math.ceil(reward.value).toLocaleString();

      gridFare["reward"] = isFiatCurrency
        ? `${reward.currency}${formattedValue}`
        : `${formattedValue} ${reward.currencyDescription || reward.currency}`;
    }

    switch (fareShelf?.rating) {
      case 0:
        fares.basic = gridFare;
        break;
      case 1:
        fares.standard = gridFare;
        break;
      case 2:
        fares.enhanced = gridFare;
        break;
      case 3:
        fares.premium = gridFare;
        break;
      case 4:
        fares.luxury = gridFare;
        break;
    }
  });
  return fares;
}

export const getHopperFareRatingName = (rating?: number) => {
  switch (rating) {
    case 0:
      return "BasicEconomy";
    case 1:
      return "Economy";
    case 2:
      return "PremiumEconomy";
    case 3:
      return "Business";
    case 4:
      return "First";
    default:
      return "";
  }
};

export function getTags(fareScore: FareScore) {
  const tags = [];
  if (fareScore.isCheapest) {
    tags.push(AlgomerchTag.Cheapest);
  }
  if (fareScore.isFastest) {
    tags.push(AlgomerchTag.Fastest);
  }
  if (fareScore.isBest) {
    tags.push(AlgomerchTag.BestFlight);
  }
  if (fareScore.isBestQuality) {
    tags.push(AlgomerchTag.BestQuality);
  }
  return tags;
}

export const mapAlgomerchTexts: { [key in AlgomerchTag]: string } = {
  BestFlight: "Best Overall",
  Cheapest: "Best Price",
  BestQuality: "Best Quality",
  Fastest: "Fastest",
};

export const isSouthwestAirlines = (airlineCode: string) =>
  airlineCode === "WN";
